import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { useModalError } from "@/hooks/use-modal-error";
import { archiveStudySchema, HandleSaveArchiveValues, Study } from "@/api/studio/studies/types/study.type";
import { useArchiveStudy } from "@/api/studio/studies/queries";

/* Prop Types */
interface ArchiveStudyProps {
  projectId: string;
  study: Study;
}

/*
 * ArchiveStudyModal component
 * This component is used to archive a study
 */
export const ArchiveStudyModal = ({ projectId, study }: ArchiveStudyProps) => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: archiveStudy, isPending: isUpdateStudyPending, isError: isUpdateStudyError } = useArchiveStudy();

  /* Hooks */
  const { close } = useModalError({ error: isUpdateStudyError });

  /* Handlers */
  const handleSave = async (values: HandleSaveArchiveValues) => {
    await archiveStudy({ projectId, studyId: study.id, values: { ...study, isArchived: values.isArchived } });
    navigate("/projects");
    close();
  };

  /* Render */
  return (
    <Form onSubmit={handleSave} schema={archiveStudySchema} options={{ defaultValues: { isArchived: true } }}>
      {({ register }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2>Archive study</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <div className="pb-4">
            <p>This study will be archived. You can find it later in the ‘Archived’ tab on the project page. You can restore it anytime.</p>
          </div>
          <TextInput type="hidden" registration={register("isArchived")} className="mb-3" />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="primary" disabled={isUpdateStudyPending}>
              {isUpdateStudyPending ? "Archiving..." : "Archive study"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
